import { EventLayout } from './eventLayout';

/**
 * Event themes -- the PART 2 token layer of docs/event-page-spec.md. The
 * chosen preset is stored on the event as `cover_preset`, so the owner and
 * every guest see it.
 *
 * Additive by construction: `plain` resolves to the app's current background
 * (the tg-* tokens, untouched), and the six art presets add their own backdrop
 * ramp and ink without redefining anything that exists.
 */

/** Panel order, left to right. */
export const EVENT_THEME_PRESETS = [
  'plain',
  'party',
  'love',
  'travel',
  'nature',
  'film',
  'drinks',
] as const;

export type EventThemePreset = (typeof EVENT_THEME_PRESETS)[number];
export type EventArtPreset = Exclude<EventThemePreset, 'plain'>;
export type EventThemeScheme = 'dark' | 'light';

/** Two stops, drawn at 165deg. The first is also the header colour. */
export type EventRamp = readonly [string, string];

/**
 * `event/backdrop`. Light ramps: the design's originals darkened in HSL
 * lightness only until white text works on them (every art theme sets its
 * on-ramp text in white) -- the least darkening that gets the title to 3:1,
 * small text ~3.1:1 -- then softened: OKLCH chroma cut by 25% at the same hue,
 * with lightness nudged (<= 0.008) so each stop keeps that luminance, and so
 * that white contrast.
 *
 * The art presets only: plain is not a value here but the
 * current `secondary_bg_color`, so it cannot drift from the rest of the app.
 * (The design proposed #EFEFF4 / #17181C→#101114 for plain; light matches
 * the token, dark does not -- the app paints #1C1C1D -- and the token wins.)
 */
export const EVENT_BACKDROP: Record<
  EventArtPreset,
  Record<EventThemeScheme, EventRamp>
> = {
  drinks: { dark: ['#3E2E12', '#2A1D0A'], light: ['#D0A456', '#7C5C37'] },
  film: { dark: ['#2C1748', '#1D0E33'], light: ['#8258BD', '#47367C'] },
  love: { dark: ['#4E1F2C', '#331119'], light: ['#E9816F', '#D85366'] },
  // Nature dark is the design's #1C4A36 -> #0F2B1F at 0.75 of its lightness:
  // at the original, no veil both separated the islands (>= 1.22:1) and kept
  // the accent and destructive icons inside them at 3:1.
  nature: { dark: ['#153828', '#0B2017'], light: ['#5CAA8D', '#419163'] },
  party: { dark: ['#4A2C12', '#331608'], light: ['#E68F4C', '#B84E35'] },
  travel: { dark: ['#123A5C', '#0A2438'], light: ['#5FA4E6', '#3C82B5'] },
};

/** The ramp for a preset and scheme. */
export function getArtRamp(
  preset: EventArtPreset,
  scheme: EventThemeScheme,
): EventRamp {
  return EVENT_BACKDROP[preset][scheme];
}

/**
 * `event/surface`: the material of the surfaces on the backdrop (islands, the
 * RSVP card, share and owner circles) on an art theme. Plain has no entry:
 * its surfaces stay exactly as they are.
 *
 * - `fill`: the alpha of a white fill. Light: 84%, a clearly visible but
 *   calm tint; film 90%, because on its darker violet the destructive red
 *   (#FF3A30, not a grey) needs that to stay at 3:1. Dark: a light veil, not
 *   a translucent dark fill, which would sink into the ramp; 7-12%, the
 *   least that keeps every surface at >= 1.22:1 against its ramp (plain's
 *   #2C2C2E on #1C1C1D). Nature's dark ramp is darkened so the same
 *   veil keeps the accent and destructive icons at 3:1.
 * - `hint`: the hint grey inside the surfaces (chevrons, "Organiser", status
 *   text), and `icon`: the section-header grey of leading glyphs. A neutral
 *   grey reads as dirt on the coloured tint, so both are tinted ink: today's
 *   grey in OKLCH with its lightness kept, hue set to the preset's (the
 *   ramp's first stop) and chroma 0.05 (0.03 still read grey on the light
 *   tints). Lightness is nudged (<= 0.02) only where needed to keep today's
 *   contrast and the floors -- hint >= 4.5:1, icon >= 3:1 -- wherever on the
 *   ramp the surface is scrolled to. Today's greys, for reference: hint
 *   ~#656569 in light (#8E8E93 is only 3.26:1 even on white), ~#ABABB0 in
 *   dark; icon #6D6D72 / #8D8E93.
 *
 * Applied as scoped tokens under [data-event-surface] (app/styles.css), so
 * the rest of the app keeps today's greys.
 */
export type EventSurfaceMaterial = {
  /**
   * Light only: the relative luminance of the darkest pixel measured under
   * an accent icon (share circles and the islands' plus icons). The accent is
   * darkened just enough to reach 4.5:1 against it, everywhere in the
   * surfaces, so blue stays one colour on the page.
   */
  accentBackdrop?: number;
  fill: number;
  hint: string;
  icon: string;
  /**
   * Dark only: the relative luminance of the brightest pixel measured under
   * a share circle's icon (veil over the blurred glyphs). The share-circle
   * accent is lifted just enough to reach 3:1 against it.
   */
  shareBackdrop?: number;
};

/**
 * The accent a themed page starts from, in both schemes, instead of
 * Telegram's `accent_text_color`: the theme owns the backdrop, so it owns the
 * blue that has to read on it. #3E88F7 is the app's own dark-scheme default
 * (shared/model/defaultTheme.ts). Stepped per preset from there -- darker on
 * the light tint, lighter on the dark share circles.
 */
export const EVENT_THEME_ACCENT = '#3E88F7';

export const EVENT_SURFACE: Record<
  EventArtPreset,
  Record<EventThemeScheme, EventSurfaceMaterial>
> = {
  drinks: {
    dark: {
      fill: 0.09,
      hint: '#C1AC8C',
      icon: '#A08C6D',
      shareBackdrop: 0.0607,
    },
    light: {
      accentBackdrop: 0.7761,
      fill: 0.84,
      hint: '#746244',
      icon: '#7D6B4C',
    },
  },
  film: {
    dark: {
      fill: 0.12,
      hint: '#ABA0C1',
      icon: '#9489AA',
      shareBackdrop: 0.0507,
    },
    light: {
      accentBackdrop: 0.8243,
      fill: 0.9,
      hint: '#6A5F7E',
      icon: '#726787',
    },
  },
  love: {
    dark: {
      fill: 0.1,
      hint: '#CCA3AB',
      icon: '#AC848C',
      shareBackdrop: 0.0655,
    },
    light: {
      accentBackdrop: 0.7776,
      fill: 0.84,
      hint: '#805B54',
      icon: '#88635C',
    },
  },
  nature: {
    dark: {
      fill: 0.08,
      hint: '#9EC4B0',
      icon: '#7DA28E',
      shareBackdrop: 0.0653,
    },
    light: {
      accentBackdrop: 0.8036,
      fill: 0.84,
      hint: '#486E5F',
      icon: '#4F7566',
    },
  },
  party: {
    dark: {
      fill: 0.08,
      hint: '#C9A991',
      icon: '#A78971',
      shareBackdrop: 0.0727,
    },
    light: {
      accentBackdrop: 0.7671,
      fill: 0.84,
      hint: '#7B5D49',
      icon: '#856651',
    },
  },
  travel: {
    dark: {
      fill: 0.08,
      hint: '#9CB8D3',
      icon: '#7B96B0',
      shareBackdrop: 0.0648,
    },
    light: {
      accentBackdrop: 0.7973,
      fill: 0.84,
      hint: '#4F6881',
      icon: '#577089',
    },
  },
};

export function getSurfaceMaterial(
  preset: EventArtPreset,
  scheme: EventThemeScheme,
): EventSurfaceMaterial {
  return EVENT_SURFACE[preset][scheme];
}

/**
 * Share circles in dark take the islands' material, denser: the same white
 * veil, this many points stronger, so they read as the same family and a
 * little more solid. (In light they take the islands' fill as it is.)
 *
 * Known cost: a white veil only lightens, and the circles sit over the
 * bright blurred glyphs, so the accent icon inside them is ~2.5-2.9:1 here
 * (a dark 90% fill gave ~4:1). Even over the bare ramp travel cannot reach
 * 3:1 at the islands' own veil. Open: keep the bokeh out from behind the
 * share row, or go back to a dark fill.
 */
export const EVENT_SURFACE_DENSE_DARK_EXTRA = 0.03;

/*
 * `event/ink`: text set straight on an art theme's ramp (hero, share labels)
 * is white in both schemes; the light ramps are darkened to carry it (see
 * EVENT_BACKDROP). The page sets it as --event-ink.
 */

/**
 * The panel's swatches: each art preset's light ramp, top to bottom. Plain
 * takes the TSX's grey-to-white disc instead of its own flat #EFEFF4, which on
 * the white panel inside a white ring would all but vanish (~1.1:1).
 */
export const EVENT_THEME_SWATCH: Record<EventThemePreset, string> = {
  drinks: swatch(EVENT_BACKDROP.drinks.light),
  film: swatch(EVENT_BACKDROP.film.light),
  love: swatch(EVENT_BACKDROP.love.light),
  nature: swatch(EVENT_BACKDROP.nature.light),
  party: swatch(EVENT_BACKDROP.party.light),
  plain: 'linear-gradient(135deg, #FFFFFF, #C1C1C1)',
  travel: swatch(EVENT_BACKDROP.travel.light),
};

/** Each art preset's glyphs. The first is the one the hero prefers. */
export const EVENT_THEME_EMOJI: Record<EventArtPreset, readonly string[]> = {
  drinks: ['🍷', '🥂', '🍸', '🍺'],
  film: ['🍿', '🎬', '🎟️', '🎞️'],
  love: ['❤️', '🌹', '💌', '✨'],
  nature: ['🌿', '🌸', '☀️', '🦋'],
  party: ['🎉', '🎂', '🎈', '✨'],
  travel: ['✈️', '🏝️', '🧳', '🗺️'],
};

export function isArtPreset(
  preset: EventThemePreset,
): preset is EventArtPreset {
  return preset !== 'plain';
}

export function getEventLayout(preset: EventThemePreset): EventLayout {
  return isArtPreset(preset) ? 'art' : 'neutral';
}

/**
 * The active ramp for a scheme. Plain is the flat app background, passed in
 * because it is a runtime token rather than a constant of this module.
 */
export function getBackdropRamp(
  preset: EventThemePreset,
  scheme: EventThemeScheme,
  appBackground: string,
): EventRamp {
  return isArtPreset(preset)
    ? getArtRamp(preset, scheme)
    : [appBackground, appBackground];
}

/**
 * The preset an event's `cover_preset` names, or plain for null or anything
 * this client does not know (a preset added on the server later).
 */
export function presetFromCover(
  cover: string | null | undefined,
): EventThemePreset {
  return EVENT_THEME_PRESETS.includes(cover as EventThemePreset)
    ? (cover as EventThemePreset)
    : 'plain';
}

/** What to send for a preset: null clears the event back to plain. */
export function coverFromPreset(preset: EventThemePreset) {
  return isArtPreset(preset) ? preset : null;
}

function swatch([from, to]: EventRamp) {
  return `linear-gradient(180deg, ${from}, ${to})`;
}
